<script setup>
import { reactive, onMounted, ref, computed } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idLimiteCredito: 0,
      idInstituicaoFinanceira: null,
      nomeInstituicaoFinanceira: '',
      descricao: '',
      numeroContrato: '',
      valorAprovado: 0,
      saldoUtilizado: 0,
      lstMoeda: '',
      dataValidade: '',
      isRotativa: false,
      permiteOperacoesSemSublimite: false,
      sublimites: []
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const mostrarAuditoria = ref(false)

// Listas carregadas via DicionarioMetadadosListas para exibição em tabelas
const listasCarregadas = ref({})

const accordionState = reactive({
  dadosBancarios: true,
  valoresVigencia: true,
  sublimites: true
})

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

const podeEditar = computed(() => {
  return authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO'))
})

// --- Padrão "Salvar e Continuar" (bloqueio de sublimites até o limite ser salvo) ---
const isLimiteSaved = ref(isEditing.value)
const isSavingLimiteLocal = ref(false)
const isSublimitesFrameBlocked = computed(() => !isLimiteSaved.value || !podeEditar.value)

const sublimiteEmEdicao = ref(null)
const isModalSublimiteOpen = ref(false)

const configBuscaBanco = computed(() => ({
  ...parceiroSearch,
  titulo: 'Buscar Instituição Financeira',
  filtrosFixos: {
    ...(parceiroSearch.filtrosFixos || {}),
    isInstFinanceira: true,
    idEmpresa: authStore.empresaAtiva
  }
}))

// --- CRUD de Sublimites via API individual ---

const carregarSublimites = async () => {
  if (!item.value.idLimiteCredito) return
  try {
    const response = await api.get(`/LimitesCreditoModalidades?idLimiteCredito=${item.value.idLimiteCredito}`)
    item.value.sublimites = response.data || []
  } catch (e) {
    console.warn('[LimitesCreditoCadastro] Erro ao carregar sublimites:', e)
  }
}

function iniciarNovoSublimite () {
  if (isLoading.value) return
  sublimiteEmEdicao.value = {
    _isNew: true,
    idLimiteCredito: item.value.idLimiteCredito,
    lstTipoOperacao: '',
    lstTipoFinanciamento: '',
    lstTipoHedge: '',
    lstTipoOperacaoCambio: '',
    valorSubLimite: 0
  }
  isModalSublimiteOpen.value = true
}

function editarSublimite (sub, idx) {
  if (isLoading.value) return
  sublimiteEmEdicao.value = { ...sub, _index: idx, _isNew: false }
  isModalSublimiteOpen.value = true
}

async function salvarSublimiteLocal () {
  const sub = sublimiteEmEdicao.value
  if (!sub.lstTipoOperacao || sub.valorSubLimite <= 0) {
    emit('show-notification', { message: 'Por favor, preencha o Tipo de Operação e o Valor do Sublimite.', type: 'warning' })
    return
  }

  const { _index, _isNew, ...dados } = sublimiteEmEdicao.value

  try {
    if (!_isNew && typeof _index === 'number') {
      await api.put(`/LimitesCreditoModalidades/${dados.idLimiteCreditoModalidade}`, dados)
      emit('show-notification', { message: 'Sublimite atualizado com sucesso.', type: 'success' })
    } else {
      await api.post('/LimitesCreditoModalidades', dados)
      emit('show-notification', { message: 'Sublimite criado com sucesso.', type: 'success' })
    }

    await carregarSublimites()
    isModalSublimiteOpen.value = false
    // NÃO nullificar sublimiteEmEdicao — handleBlur do CampoInput pode disparar depois
  } catch (error) {
    const data = error.response?.data
    let msg = data?.message || 'Erro ao salvar sublimite.'
    if (!msg && data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    emit('show-notification', { message: msg, type: 'error' })
  }
}

async function excluirSublimite (sub, _index) {
  try {
    await api.delete(`/LimitesCreditoModalidades/${sub.idLimiteCreditoModalidade}`)
    emit('show-notification', { message: 'Sublimite excluído com sucesso.', type: 'success' })
    await carregarSublimites()
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao excluir sublimite.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

function getTipoOperacaoTexto (id) {
  const lista = listasCarregadas.value.lstTipoOperacao || []
  const op = lista.find(t => String(t.idListaSistemaItem) === String(id) || String(t.idListaClienteItem) === String(id))
  return op ? op.textoLista : id
}

function getTipoEspecificoTexto (sub) {
  if (sub.lstTipoOperacao === 'FIN_TP_OPER_ESTRUT2' && sub.lstTipoFinanciamento) {
    const lista = listasCarregadas.value.lstTipoFinanciamento || []
    const op = lista.find(t => String(t.idListaClienteItem) === String(sub.lstTipoFinanciamento))
    return op ? op.textoLista : sub.lstTipoFinanciamento
  }
  if (sub.lstTipoOperacao === 'FIN_TP_OPER_ESTRUT1' && sub.lstTipoHedge) {
    const lista = listasCarregadas.value.lstTipoHedge || []
    const op = lista.find(t => String(t.idListaSistemaItem) === String(sub.lstTipoHedge) || String(t.idListaClienteItem) === String(sub.lstTipoHedge))
    return op ? op.textoLista : sub.lstTipoHedge
  }
  if (sub.lstTipoOperacao === 'FIN_TP_OPER_ESTRUT3' && sub.lstTipoOperacaoCambio) {
    const lista = listasCarregadas.value.lstTipoOperacaoCambio || []
    const op = lista.find(t => String(t.idListaSistemaItem) === String(sub.lstTipoOperacaoCambio) || String(t.idListaClienteItem) === String(sub.lstTipoOperacaoCambio))
    return op ? op.textoLista : sub.lstTipoOperacaoCambio
  }
  return '-'
}

function formatarValorPuro (valor) {
  try {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
  } catch (e) {
    return valor
  }
}

function toggleAccordion (key) {
  accordionState[key] = !accordionState[key]
}

// --- Padrão "Salvar e Continuar" ---

async function salvarLimiteLocalmente () {
  if (!podeEditar.value) return

  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa no topo antes de salvar.', type: 'warning' })
    return
  }

  isSavingLimiteLocal.value = true

  const payload = {
    idInstituicaoFinanceira: item.value.idInstituicaoFinanceira ? Number(item.value.idInstituicaoFinanceira) : 0,
    descricao: item.value.descricao || '',
    numeroContrato: item.value.numeroContrato || '',
    valorAprovado: item.value.valorAprovado ? Number(item.value.valorAprovado) : 0,
    saldoUtilizado: 0,
    lstMoeda: item.value.lstMoeda || '',
    dataValidade: item.value.dataValidade || null,
    isRotativa: !!item.value.isRotativa,
    permiteOperacoesSemSublimite: !!item.value.permiteOperacoesSemSublimite
  }

  try {
    const response = await api.post('/LimitesCredito', payload)
    item.value = response.data

    isLimiteSaved.value = true
    isEditing.value = true
    entityInfo.value = `Editando ID: ${item.value.idLimiteCredito}`

    emit('show-notification', { message: 'Limite salvo. Agora pode adicionar sublimites.', type: 'success' })
    accordionState.sublimites = true
  } catch (error) {
    const data = error.response?.data
    let msg = data?.message || 'Erro ao salvar o limite.'
    if (!msg && data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSavingLimiteLocal.value = false
  }
}

async function salvar () {
  if (!podeEditar.value) return

  const payload = {
    ...item.value,
    idInstituicaoFinanceira: item.value.idInstituicaoFinanceira ? Number(item.value.idInstituicaoFinanceira) : null,
    valorAprovado: item.value.valorAprovado ? Number(item.value.valorAprovado) : 0,
    saldoUtilizado: item.value.saldoUtilizado ? Number(item.value.saldoUtilizado) : 0,
    dataValidade: item.value.dataValidade || null,
    lstMoeda: item.value.lstMoeda || '',
    numeroContrato: item.value.numeroContrato || '',
    isRotativa: !!item.value.isRotativa,
    permiteOperacoesSemSublimite: !!item.value.permiteOperacoesSemSublimite
    // Sublimites gerenciados individualmente via LimitesCreditoModalidadesController
  }

  await submitForm('LimitesCredito', 'idLimiteCredito', payload)
}

const abrirAuditoria = () => {
  mostrarAuditoria.value = true
}

onMounted(async () => {
  try {
    const [moeda, operacao, financiamento, hedge, operacaoCambio] = await Promise.all([
      metadataStore.getListaPorCampo('LimiteCredito', 'lstMoeda'),
      metadataStore.getListaPorCampo('LimiteCreditoModalidade', 'lstTipoOperacao'),
      metadataStore.getListaPorCampo('LimiteCreditoModalidade', 'lstTipoFinanciamento'),
      metadataStore.getListaPorCampo('LimiteCreditoModalidade', 'lstTipoHedge'),
      metadataStore.getListaPorCampo('LimiteCreditoModalidade', 'lstTipoOperacaoCambio')
    ])
    listasCarregadas.value = {
      lstMoeda: moeda,
      lstTipoOperacao: operacao,
      lstTipoFinanciamento: financiamento,
      lstTipoHedge: hedge,
      lstTipoOperacaoCambio: operacaoCambio
    }
  } catch (e) {
    console.warn('[LimitesCreditoCadastro] Erro ao carregar listas:', e)
  }

  // Se estiver editando, carrega sublimites via API individual
  if (isEditing.value && item.value.idLimiteCredito) {
    await carregarSublimites()
  }
})
</script>

<template>
  <FormularioCadastro
    module-name="Limite de Crédito"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    :titulo-registro="item.descricao"
    :accordion-state="accordionState"
    :desabilitar-salvar="!podeEditar"
    :ocultar-salvar="!isLimiteSaved"
    label-botao-adicionar="Salvar Limite"
    @submit="salvar"
    @cancelar="handleCancel"
    @show-notification="(p) => emit('show-notification', p)"
  >
    <template #acoes-header v-if="isEditing">
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="abrirAuditoria"
        title="Ver Histórico de Alterações"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="!podeEditar" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <!-- FRAME: DADOS BANCÁRIOS -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('dadosBancarios')">
        <h5 class="mb-0"><i class="bi bi-bank me-2"></i>Dados Bancários e Descrição</h5>
        <i class="bi" :class="accordionState.dadosBancarios ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="accordionState.dadosBancarios" class="card-body border-top border-secondary-subtle">
        <div class="row g-3">
          <div class="col-md-6">
            <CampoBusca
              id="idInstituicaoFinanceira"
              label="Instituição Financeira (Banco)"
              v-model="item.idInstituicaoFinanceira"
              :configuracao-busca="configBuscaBanco"
              required
              :disabled="!podeEditar || isEditing"
            />
          </div>
          <div class="col-md-6">
            <CampoInput
              id="descricao"
              label="Descrição / Ref. Interna"
              v-model="item.descricao"
              placeholder="Ex: Contrato FINAME BNDES Itaú"
              required
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-6">
            <CampoInput
              id="numeroContrato"
              label="Número do Contrato"
              v-model="item.numeroContrato"
              placeholder="Ex: 123.456-7"
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="dataValidade"
              label="Data de Validade"
              v-model="item.dataValidade"
              type="date"
              required
              :disabled="!podeEditar"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME: VALORES E VIGÊNCIA -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('valoresVigencia')">
        <h5 class="mb-0"><i class="bi bi-currency-dollar me-2"></i>Valores e Vigência</h5>
        <i class="bi" :class="accordionState.valoresVigencia ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="accordionState.valoresVigencia" class="card-body border-top border-secondary-subtle">
        <div class="row g-3">
          <div class="col-md-3">
            <CampoBuscaLista
              id="lstMoeda"
              label="Moeda"
              v-model="item.lstMoeda"
              tabela="LimiteCredito"
              required
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="valorAprovado"
              label="Valor Aprovado"
              v-model="item.valorAprovado"
              type="moeda"
              required
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="saldoUtilizado"
              label="Saldo Utilizado"
              v-model="item.saldoUtilizado"
              type="moeda"
              :disabled="true"
              descricao-informativa="Gestão gerada de forma automática pelo ERP."
            />
          </div>

          <div class="col-md-3">
            <CampoSelect
              id="isRotativa"
              label="Limite é Rotativo?"
              v-model="item.isRotativa"
              :options="[{ id: true, texto: 'Sim' }, { id: false, texto: 'Não' }]"
              option-label="texto"
              option-value="id"
              required
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-3">
            <CampoSelect
              id="permiteOperacoesSemSublimite"
              label="Permite Operações sem Sublimite?"
              v-model="item.permiteOperacoesSemSublimite"
              :options="[{ id: true, texto: 'Sim' }, { id: false, texto: 'Não' }]"
              option-label="texto"
              option-value="id"
              required
              :disabled="!podeEditar"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Botão "Salvar Limite e Liberar Sublimites" (apenas criação) -->
    <div class="mb-4" v-if="!isLimiteSaved">
      <button
        type="button"
        class="btn btn-success px-4"
        @click="salvarLimiteLocalmente"
        :disabled="isSavingLimiteLocal || !item.descricao || !item.lstMoeda || !item.valorAprovado"
      >
        <span v-if="isSavingLimiteLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-save me-2"></i>
        Salvar Limite e Liberar Sublimites
      </button>
    </div>

    <!-- FRAME: GESTÃO DE SUBLIMITES (bloqueado até o limite ser salvo) -->
    <fieldset :disabled="isSublimitesFrameBlocked">
    <div class="card mb-3 border-primary-subtle" :class="{ 'frame-bloqueado': isSublimitesFrameBlocked }">
      <div class="card-header bg-primary-subtle d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('sublimites')">
        <h5 class="mb-0 text-primary-emphasis"><i class="bi bi-diagram-3 me-2"></i>Gestão de Sublimites por Modalidade</h5>
        <i class="bi" :class="accordionState.sublimites ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="accordionState.sublimites" class="card-body p-0 border-top border-primary-subtle">
        <div v-if="isSublimitesFrameBlocked" class="alert alert-info border-0 shadow-sm text-center m-3">
          <i class="bi bi-info-circle me-2"></i>
          Salve o Limite de Crédito primeiro para gerenciar os sublimites.
        </div>
        <div v-else class="admin-table-container border-0 shadow-none mb-0">
          <table class="table admin-table m-0">
            <thead>
              <tr>
                <th>Tipo de Operação</th>
                <th>Especificação</th>
                <th style="width: 200px;" class="text-end">Valor do Sublimite</th>
                <th class="text-end" style="width: 100px;">
                  <button v-if="podeEditar" type="button" class="btn btn-sm btn-primary py-0" @click.stop="iniciarNovoSublimite">
                    <i class="bi bi-plus-lg"></i>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(sub, index) in item.sublimites" :key="sub.idLimiteCreditoModalidade || index">
                <td class="fw-bold">{{ getTipoOperacaoTexto(sub.lstTipoOperacao) }}</td>
                <td>{{ getTipoEspecificoTexto(sub) }}</td>
                <td class="text-end">{{ formatarValorPuro(sub.valorSubLimite) }}</td>
                <td class="acoes-cell text-end pe-3">
                  <button v-if="podeEditar" type="button" class="btn-icon" @click="editarSublimite(sub, index)">
                    <i class="bi bi-pencil-fill"></i>
                  </button>
                  <button v-if="podeEditar" type="button" class="btn-icon text-danger" @click="excluirSublimite(sub, index)">
                    <i class="bi bi-trash-fill"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="!item.sublimites || item.sublimites.length === 0">
                <td colspan="4" class="text-center py-4 text-muted">
                  <i class="bi bi-info-circle me-2"></i> Nenhum sublimite específico cadastrado.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </fieldset>

    <template #footer-acoes v-if="!podeEditar">
      <button type="button" class="btn btn-secondary" @click="handleCancel">Voltar</button>
    </template>
  </FormularioCadastro>

  <ModalFormulario v-model="isModalSublimiteOpen" titulo="Manutenção de Sublimite" tamanho="md" @confirmar="salvarSublimiteLocal">
    <div v-if="sublimiteEmEdicao" class="row g-3">
      <div class="col-md-12">
        <CampoBuscaLista
          id="lstTipoOperacao"
          label="Tipo de Operação"
          v-model="sublimiteEmEdicao.lstTipoOperacao"
          tabela="LimiteCreditoModalidade"
          required
        />
      </div>
      <div class="col-md-6" v-if="sublimiteEmEdicao.lstTipoOperacao === 'FIN_TP_OPER_ESTRUT2'">
        <CampoBuscaLista
          id="lstTipoFinanciamento"
          label="Tipo de Financiamento"
          v-model="sublimiteEmEdicao.lstTipoFinanciamento"
          tabela="LimiteCreditoModalidade"
        />
      </div>
      <div class="col-md-6" v-if="sublimiteEmEdicao.lstTipoOperacao === 'FIN_TP_OPER_ESTRUT1'">
        <CampoBuscaLista
          id="lstTipoHedge"
          label="Tipo de Hedge"
          v-model="sublimiteEmEdicao.lstTipoHedge"
          tabela="LimiteCreditoModalidade"
        />
      </div>
      <div class="col-md-6" v-if="sublimiteEmEdicao.lstTipoOperacao === 'FIN_TP_OPER_ESTRUT3'">
        <CampoBuscaLista
          id="lstTipoOperacaoCambio"
          label="Tipo de Câmbio"
          v-model="sublimiteEmEdicao.lstTipoOperacaoCambio"
          tabela="LimiteCreditoModalidade"
        />
      </div>
      <div class="col-md-6">
        <CampoInput
          id="valorSubLimite"
          label="Valor do Sublimite"
          v-model="sublimiteEmEdicao.valorSubLimite"
          type="moeda"
          required
        />
      </div>
    </div>
  </ModalFormulario>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="LimiteCredito"
    :chave-primaria="item.idLimiteCredito"
    :titulo-registro="item.descricao"
    :tabelas-filhas="['LimiteCreditoModalidade']"
    :chave-estrangeira="'IdLimiteCredito'"
    contexto="tenant"
    :dicionarios="{ 'lstMoeda': listasCarregadas['lstMoeda'] || [], 'lstTipoOperacao': listasCarregadas['lstTipoOperacao'] || [] }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
.pointer { cursor: pointer; }

.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.25rem;
  overflow-x: auto;
}
.admin-table thead th {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-heading-color);
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--bs-border-color);
}
.admin-table td {
  border-bottom: 1px solid var(--bs-border-color);
  color: var(--bs-body-color);
  padding: 0.5rem 0.75rem;
}

.acoes-cell { text-align: right; vertical-align: middle; }
.btn-icon { background: none; border: none; color: #6c7d8e; padding: 0.4rem; transition: 0.2s; }
.btn-icon:hover { color: #212934; }
[data-bs-theme="dark"] .btn-icon:hover { color: #fff; }
</style>
